import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  schema?: object;
}

export const useSEO = ({ title, description, keywords, canonical, schema }: SEOProps) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const updatePropertyTag = (property: string, content: string) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
      updatePropertyTag('og:description', description);
      updateMetaTag('twitter:description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    if (title) {
      updatePropertyTag('og:title', title);
      updateMetaTag('twitter:title', title);
    }

    // Update canonical
    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
      link.href = `${siteUrl}${canonical}`;
      
      updatePropertyTag('og:url', `${siteUrl}${canonical}`);
    }

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Add Schema.org JSON-LD
    if (schema) {
      const scriptId = 'schema-org-jsonld';
      let script = document.getElementById(scriptId);
      
      if (!script) {
        script = document.createElement('script');
        script.id = scriptId;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      
      const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
      const schemaWithUrl = JSON.parse(JSON.stringify(schema).replace(/SITE_URL/g, siteUrl));
      script.textContent = JSON.stringify(schemaWithUrl);
    }

    return () => {
      // Cleanup schema on unmount
      const script = document.getElementById('schema-org-jsonld');
      if (script) {
        script.remove();
      }
    };
  }, [title, description, keywords, canonical, schema]);
};

export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Системный маркетинг и AI',
  url: 'SITE_URL',
  logo: 'https://static.readdy.ai/image/621f76ab0c5d0ae7827482fe9856efef/d957a6b7765ec2d313d91cb8edd75d6a.png',
  description: 'Системный маркетинг и AI-технологии для стабильного роста продаж',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Москва',
    addressCountry: 'RU'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '55.751244',
    longitude: '37.618423'
  },
  areaServed: {
    '@type': 'City',
    name: 'Москва'
  },
  sameAs: []
});

export const getWebPageSchema = (name: string, description: string, url: string = 'SITE_URL') => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name,
  description,
  url,
  inLanguage: 'ru-RU',
  isPartOf: {
    '@type': 'WebSite',
    name: 'Системный маркетинг и AI',
    url: 'SITE_URL'
  }
});

export const getServiceSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Маркетинговые услуги',
  provider: {
    '@type': 'Organization',
    name: 'Системный маркетинг и AI'
  },
  areaServed: {
    '@type': 'City',
    name: 'Москва'
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Маркетинговые услуги',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Контекстная реклама',
          description: 'Настройка и оптимизация контекстной рекламы с высоким ROMI'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Таргетированная реклама',
          description: 'Точечный таргетинг для максимальной эффективности'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'SMM',
          description: 'Управление социальными сетями и контент-маркетинг'
        }
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Продвижение на маркетплейсах',
          description: 'Комплексное продвижение на Ozon, Wildberries и других площадках'
        }
      }
    ]
  }
});

export const getFAQSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Как вы обеспечиваете высокий ROMI в контекстной рекламе?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Мы настраиваем сквозную аналитику для отслеживания реального дохода с каждого объявления, оптимизируем кампании по деньгам, а не по кликам, и отключаем неэффективные ключевые слова. Это позволяет достигать ROMI до 492%.'
      }
    },
    {
      '@type': 'Question',
      name: 'Какие услуги вы предоставляете?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Мы предоставляем комплексные маркетинговые услуги: контекстная реклама, таргетированная реклама, SMM, продвижение на маркетплейсах (Ozon, Wildberries), сквозная аналитика и маркетинговый консалтинг с использованием AI-технологий.'
      }
    },
    {
      '@type': 'Question',
      name: 'Сколько времени занимает запуск рекламной кампании?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Запуск базовой рекламной кампании занимает 3-5 рабочих дней. Полная настройка с внедрением сквозной аналитики и оптимизацией может занять до 2 недель для достижения максимальной эффективности.'
      }
    }
  ]
});
